<template>
  <div class="data-collection-step min-h-full">
    <div class="flex items-center gap-2 mb-4">
      <h2 class="text-lg font-medium text-gray-900">HOW | WHICH - Collecte de données</h2>
      <BookCheckIcon v-show="isStepValid" class="w-5 h-5 text-green-500" />
    </div>

    <!-- Ajout du sélecteur de modèle HOW/WHICH -->
    <InstrumentationSelector
      :user-id="modelValue.user_id"
      :read-only-config="readOnlyConfig"
      @model-selected="handleModelSelected"
      @reset="handleModelReset"
    />

    <div class="space-y-6">
      <div v-if="isLoading" class="flex justify-center items-center py-8">
        <div class="spinner"></div>
        <span class="ml-3">Chargement des sections...</span>
      </div>

      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {{ error }}
      </div>

      <div v-else-if="modelValue.selectedSchema && compiledSchemaData" class="space-y-6">
        <div v-if="rootCompiledSections.length === 0"
          class="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-700">
          Aucune section disponible pour ce schéma.
        </div>

        <div v-for="sectionData in rootCompiledSections" v-else :key="sectionData.section_id || sectionData.id">
          <RecursiveSection :compiled-section-data="sectionData" :data-entries="modelValue.data_entries" :level="0"
            :compiled-schema="compiledSchemaData" :imported-schema="importedSchema ?? null" @update:data-entry="handleDataEntry" />
        </div>
      </div>

      <div v-else class="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-700">
        Veuillez d'abord sélectionner un schéma.
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useSchemasStore } from '@/stores/schemasStore';
import type { SchemaJsonWithIds, SectionJsonWithId } from '@/utils/schemaExportUtils';
import { compileSchemaWithIds } from '@/utils/schemaExportUtils';
import { BookCheckIcon } from 'lucide-vue-next';
import type { PropType } from 'vue';
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import type { MemoDataEntry } from '../../api/types/memos/entries';
import type { MemoFormData } from '../../types/memoFormData';
import RecursiveSection from '../RecursiveSection.vue';
import InstrumentationSelector from '../selectors/InstrumentationSelector.vue';

export default defineComponent({
  name: 'DataCollectionStep',
  components: {
    RecursiveSection,
    BookCheckIcon,
    InstrumentationSelector
  },
  props: {
    modelValue: {
      type: Object as PropType<MemoFormData>,
      required: true
    },
    readOnlyConfig: {
      type: Object as PropType<Record<string, unknown> | undefined>,
      required: false,
      default: undefined
    }
  },
  emits: ['update:model-value', 'isValid'],
  setup(props, { emit }) {
    const schemasStore = useSchemasStore();
    const isLoading = ref(false);
    const error = ref<string | null>(null);
    const importedSchema = ref<SchemaJsonWithIds | null>(null);

    // Computed property pour vérifier si l'étape est valide
    const isStepValid = computed(() => {
      return props.modelValue.data_entries && props.modelValue.data_entries.length > 0;
    });

    // Watch pour émettre l'état de validité quand il change
    watch(isStepValid, (newValue) => {
      emit('isValid', newValue);
    }, { immediate: true });

    // Calcul du schéma compilé avec traductions prioritaires
    const compiledSchemaData = computed(() => {
      if (!props.modelValue.selectedSchema?.uuid) {
        return null;
      }

      try {
        // Trouver le schéma complet dans le store
        const fullSchema = schemasStore.schemas.find(s => s.uuid === props.modelValue.selectedSchema?.uuid);
        if (!fullSchema) {
          console.error("❌ Schéma non trouvé dans le store");
          return null;
        }

        // Utiliser les données du store pour compiler le schéma avec traductions
        const compiledSchema = compileSchemaWithIds(
          fullSchema,
          schemasStore.sections,
          schemasStore.translations,
          schemasStore.schemaTranslations,
          schemasStore.fieldConfigs as any
        );

        return compiledSchema;
      } catch (err) {
        console.error("❌ Erreur lors de la compilation du schéma:", err);
        return null;
      }
    });

    // Sections racines (niveau 0) du schéma compilé - DIRECT depuis le JSON !
    const rootCompiledSections = computed((): SectionJsonWithId[] => {
      if (!compiledSchemaData.value?.section_list) {
        return [];
      }

      const roots = compiledSchemaData.value.section_list.filter(section =>
        (section.indentationLevel || 0) === 0
      );

      return roots;
    });

    // Initialiser les données du store si nécessaire
    const initializeStoreData = async (): Promise<void> => {
      if (!schemasStore.schemas.length || !schemasStore.sections.length) {
        isLoading.value = true;
        error.value = null;

        try {
          await schemasStore.initializeSchemasData();
        } catch (err) {
          console.error("❌ Erreur lors de l'initialisation des données du store:", err);
          error.value = "Erreur lors du chargement des données";
        } finally {
          isLoading.value = false;
        }
      }
    };

    // Charger les données au montage et quand le schéma change
    onMounted(initializeStoreData);

    watch(() => props.modelValue.selectedSchema?.uuid, async () => {
      if (props.modelValue.selectedSchema?.uuid && !compiledSchemaData.value) {
        await initializeStoreData();
      }
    });

    const handleDataEntry = (entry: MemoDataEntry): void => {
      if (!props.modelValue.data_entries || !Array.isArray(props.modelValue.data_entries)) {
        console.error("❌ data_entries n'est pas un tableau");
        return;
      }

      // Si c'est HOW.Technic, on met directement l'appellation
      if (entry.name === 'HOW.Technic') {
        entry.value = props.modelValue.selectedSchema?.appellation || '';
      }

      const updatedEntries = [...props.modelValue.data_entries];
      const existingIndex = updatedEntries.findIndex(e => e.name === entry.name);

      if (existingIndex !== -1) {
        updatedEntries[existingIndex] = entry;
      } else {
        updatedEntries.push(entry);
      }

      emit('update:model-value', {
        ...props.modelValue,
        data_entries: updatedEntries
      });

      emit('isValid', updatedEntries.length > 0);
    };

    // Injection des data_entries du modèle sélectionné
    function handleModelSelected(payload: { data_entries: MemoDataEntry[], compiledSchema: SchemaJsonWithIds | null }): void {
      if (!payload.data_entries && !payload.compiledSchema) return;
      // Cas reset : vider importedSchema et ne rien fusionner
      if (payload.data_entries.length === 0 && !payload.compiledSchema) {
        importedSchema.value = null;
        emit('update:model-value', {
          ...props.modelValue,
          // On ne touche pas aux data_entries du mémo courant
        });
        return;
      }
      // On clone l'existant pour éviter les effets de bord
      const updatedEntries = [...props.modelValue.data_entries];
      for (const entry of payload.data_entries) {
        const idx = updatedEntries.findIndex(e => e.name === entry.name);
        if (idx !== -1) {
          updatedEntries[idx] = { ...entry };
        } else {
          updatedEntries.push({ ...entry });
        }
      }
      emit('update:model-value', {
        ...props.modelValue,
        data_entries: updatedEntries
      });
      importedSchema.value = payload.compiledSchema;
    }
    function handleModelReset(): void {
      // Ne fait rien sur les data_entries, juste UX
    }

    return {
      rootCompiledSections,
      handleDataEntry,
      isLoading,
      error,
      compiledSchemaData,
      isStepValid,
      handleModelSelected,
      handleModelReset,
      importedSchema
    };
  }
});
</script>

<style scoped>
.data-collection-step {
  min-height: 200px;
}
</style>
